import React, { useEffect, useMemo, useState } from 'react';
import { ResultTable } from '../shared/blocks/ResultBlocks';
import { ui } from './canvasUi';

/**
 * ResultTable with a pager under it, for the canvas surface only.
 *
 * WHY THIS WRAPS ResultTable RATHER THAN CHANGING IT
 *
 * `ResultTable` (components/shared/blocks/ResultBlocks.js) is shared with the
 * chat surface, where a result table is a short answer inside a conversation
 * and paging it would be noise. A canvas block is a fixed-height dashboard
 * tile that can hold hundreds of rows, so it needs paging and chat does not.
 * Wrapping keeps the change on the surface that asked for it.
 *
 * WHY EXPORTS ARE UNAFFECTED
 *
 * This component slices for DISPLAY only; it never touches `block.result`.
 * Every export path (canvasExport.js for Excel/PDF, canvasPptx.js for
 * PowerPoint and Google Slides, canvasEmailHtml.js server-side) builds from
 * `result.rows`, not from the rendered DOM, so they all keep receiving every
 * row. Any future "export what's on screen" change would silently start
 * exporting one page of 25 — don't.
 */

const PAGE_SIZE = 25;

const s = {
    wrap: { display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 },
    bar: {
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px',
        fontSize: '11.5px', color: ui.ink2, fontVariantNumeric: 'tabular-nums',
        paddingTop: '2px', flexShrink: 0,
    },
    btn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: '26px', height: '24px', padding: '0 8px',
        borderRadius: ui.radiusSm, cursor: 'pointer', fontFamily: 'inherit',
        fontSize: '11.5px', fontWeight: 600,
    },
    btnOff: { opacity: 0.4, cursor: 'default' },
    count: { marginRight: 'auto', paddingLeft: '2px' },
};

export default function PaginatedTable({ rows, pageSize = PAGE_SIZE }) {
    const list = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
    const total = list.length;
    const pages = Math.max(1, Math.ceil(total / pageSize));
    const unionKeys = useMemo(
        () => [...new Set(list.flatMap((r) => (r && typeof r === 'object' ? Object.keys(r) : [])))],
        [list],
    );
    const [page, setPage] = useState(0);

    // A re-run can return fewer rows than the page the user was on. Without
    // this the card would render an empty table with no obvious way back —
    // clamp instead of resetting to 0, so a refresh that returns a similar
    // number of rows keeps the reader roughly where they were.
    useEffect(() => {
        setPage((p) => Math.min(p, pages - 1));
    }, [pages]);

    if (total <= pageSize) return <ResultTable rows={list} groupNumbers />;

    const safePage = Math.min(page, pages - 1);
    const start = safePage * pageSize;
    // Padded to the UNION of keys across every row, not just this page's.
    // ResultTable derives its headers from `Object.keys(rows[0])`, so with
    // ragged rows (merged sources, agent viz tables) the columns would change
    // between pages depending on which row happened to land first. Padding
    // keeps one stable set of columns for the whole table.
    const shown = list.slice(start, start + pageSize).map((row) => {
        const padded = {};
        for (const k of unionKeys) padded[k] = row?.[k] ?? '';
        return padded;
    });

    const atFirst = safePage === 0;
    const atLast = safePage >= pages - 1;

    return (
        <div style={s.wrap} data-testid="canvas-paginated-table">
            <ResultTable rows={shown} groupNumbers />
            <div style={s.bar}>
                <span style={s.count} data-testid="canvas-page-count">
                    {start + 1}–{start + shown.length} of {total}
                </span>
                <button type="button" className="cvx-ghost cvx-focus"
                    style={{ ...s.btn, ...(atFirst ? s.btnOff : {}) }}
                    onClick={() => setPage(safePage - 1)} disabled={atFirst}
                    aria-label="Previous page" data-testid="canvas-page-prev">
                    ‹
                </button>
                <span aria-live="polite">{safePage + 1} / {pages}</span>
                <button type="button" className="cvx-ghost cvx-focus"
                    style={{ ...s.btn, ...(atLast ? s.btnOff : {}) }}
                    onClick={() => setPage(safePage + 1)} disabled={atLast}
                    aria-label="Next page" data-testid="canvas-page-next">
                    ›
                </button>
            </div>
        </div>
    );
}
